import React from "react";
import { ApolloError } from "@apollo/client";
import { DataCell } from "@/components/DataCell";
import { PercentChange } from "@/components/DataCell/PercentChange";
import { useTranslation } from "react-i18next";
import { useContextMetrics } from "@/hooks/useContextMetrics";
import { useCurrentCohortYear } from "@/hooks/useCurrentCohortYear";
import { useOrgPermissions } from "@/hooks/useOrgPermissions";
import {
  PartnerFeatureEnum,
  OrganizationMetricsContextEnum,
} from "@/__generated__/types";
import { PredictiveModelType } from "./types";

interface DataCellsSectionProps {
  orgId: string | undefined;
  orgIdLoading: boolean;
  orgIdError: ApolloError | undefined;
  cohortLoading: boolean;
  cohortError: ApolloError | undefined;
  modelType?: PredictiveModelType;
}

const getContextForModelType = (
  modelType: PredictiveModelType,
  hasRise: boolean,
): OrganizationMetricsContextEnum => {
  if (!hasRise) {
    return OrganizationMetricsContextEnum.PredictiveModelsCommitIndex;
  }

  switch (modelType) {
    case "Apply":
      return OrganizationMetricsContextEnum.RisePredictiveModelsApplyIndex;
    case "Commit":
      return OrganizationMetricsContextEnum.RisePredictiveModelsCommitIndex;
    case "Melt":
      return OrganizationMetricsContextEnum.RisePredictiveModelsMeltIndex;
    case "Yield":
      return OrganizationMetricsContextEnum.RisePredictiveModelsYieldIndex;
    default:
      return OrganizationMetricsContextEnum.RisePredictiveModelsCommitIndex;
  }
};

export const DataCellsSection: React.FC<DataCellsSectionProps> = ({
  orgId,
  orgIdLoading,
  orgIdError,
  cohortLoading,
  cohortError,
  modelType = "Commit",
}) => {
  const { t } = useTranslation();
  const { hasOrgFeature } = useOrgPermissions();
  const hasRise = hasOrgFeature(PartnerFeatureEnum.RisePredictiveModels);

  const context = getContextForModelType(modelType, hasRise);

  const {
    loading: metricsLoading,
    error: metricsError,
    getMetricDisplayValue,
    getPreviousCycleValue,
    getMetricCalculatedPercentChanged,
  } = useContextMetrics(orgId, context);

  const isLoading = orgIdLoading || cohortLoading;
  const hasError = orgIdError || cohortError;
  const hasRequiredData = Boolean(orgId);

  const { cohortYear } = useCurrentCohortYear();

  const showLoading = () => {
    if (hasError || metricsError) return false;
    return isLoading || metricsLoading;
  };

  const showError = () => {
    return hasError || metricsError || (!hasRequiredData && !isLoading);
  };

  const getTitleForCommittedHighCount = () => {
    if (modelType === "Apply") {
      return t("predictiveModels.bentos.committedHighCount.title.apply");
    }
    return t("predictiveModels.bentos.committedHighCount.title.default");
  };

  const getTooltipForCommittedHighCount = () => {
    if (!hasRise) {
      return t("predictiveModels.bentos.committedHighCount.tooltip.commit");
    }

    if (modelType === "Apply") {
      return t("predictiveModels.bentos.committedHighCount.tooltip.apply");
    }

    return t("predictiveModels.bentos.committedHighCount.tooltip.riseDefault", {
      type: modelType.toLowerCase(),
    });
  };

  const errorContent = (
    <span className="text-sm text-muted-foreground">
      {t("predictiveModels.bentos.error.loadingError")}
    </span>
  );

  const cell1Key = hasRise ? "totalStudents" : "totalHighIndexStudents";
  const cell2Key = hasRise
    ? "funnelStateTotalStudentsV2"
    : "committedHighIndexStudentsV2";

  return (
    <div>
      {/* Title Section */}
      <h2 className="mb-2 text-sm leading-5 font-normal text-muted-foreground">
        {t("predictiveModels.bentos.title", {
          cohort: cohortYear,
        })}
      </h2>

      {/* Data Cells Grid */}
      <div className="mb-6 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
        <DataCell
          className="min-h-[136px]"
          isLoading={showLoading()}
          title={t("predictiveModels.bentos.highCount.title")}
          tooltipText={t("predictiveModels.bentos.highCount.tooltip", {
            type: modelType.toLowerCase(),
          })}
          middleValue={
            showError()
              ? errorContent
              : getMetricDisplayValue(cell1Key, false, true)
          }
          bottomValue={
            showError() ? null : (
              <PercentChange
                value={getMetricCalculatedPercentChanged(cell1Key)}
                prevCount={getPreviousCycleValue(cell1Key)}
                customTemplate={t("metrics.comparisons.yearOverYear", {
                  percentChanged: "{{percentChange}}",
                })}
              />
            )
          }
        />

        <DataCell
          className="min-h-[136px]"
          isLoading={showLoading()}
          title={getTitleForCommittedHighCount()}
          tooltipText={getTooltipForCommittedHighCount()}
          middleValue={
            showError()
              ? errorContent
              : getMetricDisplayValue(cell2Key, false, true)
          }
          bottomValue={
            showError() ? null : (
              <PercentChange
                value={getMetricCalculatedPercentChanged(cell2Key)}
                prevCount={getPreviousCycleValue(cell2Key)}
                zeroChangeKey="metrics.comparisons.vsZeroLastYear"
                customTemplate={t("metrics.comparisons.yearOverYear", {
                  percentChanged: "{{percentChange}}",
                })}
              />
            )
          }
        />

        <DataCell
          className="min-h-[136px]"
          isLoading={showLoading()}
          title={t("predictiveModels.bentos.upTrendCount.title")}
          tooltipText={t("predictiveModels.bentos.upTrendCount.tooltip", {
            type: modelType.toLowerCase(),
          })}
          middleValue={
            showError()
              ? errorContent
              : getMetricDisplayValue("upTrendingStudents", false, true)
          }
          bottomValue={null}
        />

        <DataCell
          className="min-h-[136px]"
          isLoading={showLoading()}
          title={t("predictiveModels.bentos.downTrendCount.title")}
          tooltipText={t("predictiveModels.bentos.downTrendCount.tooltip", {
            type: modelType.toLowerCase(),
          })}
          middleValue={
            showError()
              ? errorContent
              : getMetricDisplayValue("downTrendingStudents", false, true)
          }
          bottomValue={null}
        />
      </div>
    </div>
  );
};
